import React, { useState, useEffect } from 'react';
import StableMuiDialog from '../../../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import {
  colors,
  DIALOG_PAPER_SX,
  CANCEL_BUTTON_SX,
  PRIMARY_BUTTON_SX,
  SectionLabel,
  MethodCard,
  OptionsPanel,
} from '../../Offers/TownPortalFlow/TownPortalOffersDialog.js';
import { ToggleRow } from '../../Offers/TownPortalFlow/LinkPermissionOptions.js';
import axiosInstance from '../../../../fetchConfig/axiosConfig.js';
import { useUser, TOWN_PORTAL_USER_ID } from '../../../UserContext.js';
import { usePersistedDataStore } from '../../../store.js';
import { toMySQLDateTime } from '../../../../common/helper_functions.js';
import { toast } from '../../../../common/toast';
import logger from '../../../../utils/logger';
import { isTownViewDevNoPersist } from '../../../../utils/townViewDevMode.js';

// Names and requiresDateDue mirror the Aventine DocType registry (GET /api/doc-requests/doc-types).
const DOC_TYPES = [
  { docType: 'SoleAuth', name: 'Sole Authorization', townName: 'Multiple Grievance Filings', description: 'Client authorizes Aventine as sole representative', townDescription: 'More than one grievance was filed for this property', icon: <DescriptionOutlinedIcon fontSize="inherit" /> },
  { docType: 'ProofOccupancy', name: 'Residency Affidavit', townName: 'I believe the Homeowner does not live at the property', description: 'Client affirms residence, or LLC membership if entity owned', townDescription: 'The owner does not occupy the property', icon: <HomeOutlinedIcon fontSize="inherit" /> },
  { docType: 'ProofLLC', name: 'Trust/LLC docs', townName: 'Corporate Entity (Trust / LLC) Owner', description: 'Client provides trust or LLC ownership documents', townDescription: 'The property is owned by a trust or LLC', icon: <AccountBalanceOutlinedIcon fontSize="inherit" /> },
  { docType: 'CommAuth', name: 'Commercial Authorization', description: 'Foxit authorization for commercial parcels', icon: <BusinessOutlinedIcon fontSize="inherit" />, requiresDateDue: true },
];

const FIELD_SX = { '& .MuiInputBase-root': { fontSize: 12, borderRadius: '6px' }, '& .MuiInputBase-input': { py: 0.75 } };
const EMAIL_RE = /^\S+@\S+\.\S+$/;
const RECIPIENT_FIELDS = [['name', 'Client name'], ['email', 'Client email']];
const SKIP_REASONS = {
  already_requested: 'already requested',
  case_settled: 'case settled',
  not_eligible: 'not eligible',
  court_date_passed: 'court date passed',
};

const DocRequestDialog = ({ open, onClose, rows, negotiationObj, townView = false }) => {
  const { user } = useUser();
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [dueDate, setDueDate] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [recipients, setRecipients] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestUserId = townView ? TOWN_PORTAL_USER_ID : Number(user?.userId);

  useEffect(() => {
    if (!open) return;
    setDocType(DOC_TYPES[0]);
    setDueDate('');
    setIsTest(false);
    setRecipients({});
    if (isTownViewDevNoPersist(townView)) {
      setLoadingContacts(false);
      return undefined;
    }
    setLoadingContacts(true);

    let cancelled = false;
    axiosInstance.post('/doc-requests/client-contacts', { parcelIds: rows.map((row) => row.parcel_id), userId: requestUserId })
      .then(({ data }) => data.contacts || {})
      .catch((error) => {
        logger.error('Failed to load doc request client contacts', error);
        return {};
      })
      .then((contacts) => {
        if (cancelled) return;
        setRecipients(Object.fromEntries(rows.map((row) => [
          row.parcel_id,
          { name: contacts[row.parcel_id]?.name || row.ClientName || '', email: contacts[row.parcel_id]?.email || '' },
        ])));
        setLoadingContacts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, requestUserId, townView]);

  const testMode = isTest;
  const devStoreOnly = isTownViewDevNoPersist(townView);
  const activeRecipients = testMode
    ? Object.fromEntries(rows.map((row) => [row.parcel_id, { name: user?.userName || '', email: user?.email || '' }]))
    : recipients;
  const invalidEmailIds = rows.map((row) => row.parcel_id).filter((pid) => !EMAIL_RE.test((activeRecipients[pid]?.email || '').trim()));
  const missingEmailCount = invalidEmailIds.length;
  const visibleDocTypes = townView ? DOC_TYPES.filter((type) => type.townName) : DOC_TYPES;
  const displayName = (type) => (townView && type.townName) || type.name;
  const missingDueDate = !townView && docType.requiresDateDue && !dueDate;
  const canSubmit = rows.length > 0 && !missingDueDate && !submitting && (devStoreOnly || (!missingEmailCount && !loadingContacts));

  const handleSubmit = async () => {
    setSubmitting(true);
    const requestedFrom = Object.fromEntries(rows.map((row) => [
      row.parcel_id,
      { name: activeRecipients[row.parcel_id]?.name?.trim() || null, email: activeRecipients[row.parcel_id]?.email?.trim() || '' },
    ]));
    try {
      let data = { created: [], skipped: [], errors: [] };
      let createdIds;
      if (devStoreOnly) {
        logger.log('[TownView dev] Skipping doc-requests/batch — FE store only');
        createdIds = Object.fromEntries(rows.map((row) => [row.parcel_id, `local-${row.parcel_id}-${Date.now()}`]));
      } else {
        ({ data } = await axiosInstance.post('/doc-requests/batch', {
          userId: requestUserId,
          requests: rows.map((row) => ({
            municode: negotiationObj.MuniCode,
            year: Number(negotiationObj.TaxYear),
            parcel_id: row.parcel_id,
            from_party: { type: 'aventine' },
            to_party: { type: 'client', ...requestedFrom[row.parcel_id] },
            deliverable: { doc_type: docType.docType, name: docType.name },
            date_due: !townView && dueDate ? `${dueDate}T00:00:00` : null,
            is_test: testMode,
          })),
        }));
        logger.log('Doc request batch result', data);
        createdIds = Object.fromEntries((data.created || []).map((item) => [item.parcel_id, item.request_id]));
      }
      const { negotiationObj: current, negotiationObjVersion, setNegotiationObj, setNegotiationObjVersion } = usePersistedDataStore.getState();
      const dateRequested = toMySQLDateTime(new Date());
      setNegotiationObj({
        ...current,
        cases: current.cases.map((caseItem) => (caseItem.parcel_id in createdIds ? {
          ...caseItem,
          DocRequests: [...(caseItem.DocRequests || []), {
            dateCompleted: null,
            dateRequested,
            docType: docType.docType,
            reminders: { dateNext: null, history: [], lastSent: null, numSent: 0, paused: false },
            requestId: createdIds[caseItem.parcel_id],
            requestedBy: user?.userName || null,
            requestedFrom: requestedFrom[caseItem.parcel_id],
            status: 'pending',
          }],
        } : caseItem)),
      });
      setNegotiationObjVersion(negotiationObjVersion + 1);
      const skipped = data.skipped || [];
      const errors = data.errors || [];
      const createdCount = Object.keys(createdIds).length;
      const summary = `${createdCount} ${displayName(docType)} request${createdCount === 1 ? '' : 's'} created`;
      const notes = [
        skipped.length ? `${skipped.length} skipped (${[...new Set(skipped.map((item) => SKIP_REASONS[item.reason] || item.reason))].join(', ')})` : '',
        errors.length ? `${errors.length} not created (${[...new Set(errors.map((item) => item.reason))].join(', ')})` : '',
      ].filter(Boolean);
      if (notes.length) {
        toast.warning(`${summary}, ${notes.join(', ')}`);
      } else {
        toast.success(summary);
      }
      onClose();
    } catch (error) {
      logger.error('Doc request batch failed', error);
      toast.error(error?.response?.data?.error || error.message || 'Failed to create document requests');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...DIALOG_PAPER_SX, maxWidth: 600 } }}
    >
      <Box sx={{ p: 3 }}>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}>
          {townView ? 'Flag Issue' : 'Flag jurisdictional issue'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: '0 0 20px' }}>
          {townView
            ? `Flag an issue for ${rows.length} selected case${rows.length === 1 ? '' : 's'}`
            : `Request documents from the client for ${rows.length} selected case${rows.length === 1 ? '' : 's'}`}
        </Typography>

        <SectionLabel>{townView ? 'Issue' : 'Document'}</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: townView ? '1fr' : 'repeat(2, 1fr)', gap: 1, mb: 2.5 }}>
          {visibleDocTypes.map((type) => (
            <MethodCard
              key={type.docType}
              active={docType.docType === type.docType}
              badge={!townView && type.requiresDateDue ? 'Due date' : null}
              icon={type.icon}
              name={displayName(type)}
              description={(townView && type.townDescription) || type.description}
              onClick={() => setDocType(type)}
            />
          ))}
        </Box>

        {!townView && <SectionLabel>Request options</SectionLabel>}
        <OptionsPanel visible={!townView}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
                {`Due date${docType.requiresDateDue ? '' : ' (optional)'}`}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
                {docType.requiresDateDue ? 'Parcels with a SCAR court date are due the day before it instead' : 'Defaults to the document type deadline'}
              </Typography>
            </Box>
            <TextField
              type="date"
              size="small"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              error={missingDueDate}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              sx={{ ...FIELD_SX, width: 160 }}
            />
          </Box>
          <ToggleRow
            name="Test request"
            description="Emails go to your own address and the request is hidden from Aventine queues"
            checked={isTest}
            onChange={(e) => setIsTest(e.target.checked)}
          />
        </OptionsPanel>

        <SectionLabel>{townView ? 'Properties' : (isTest ? 'Recipients (test mode, sent to you)' : 'Recipients')}</SectionLabel>
        <OptionsPanel visible>
          <Box sx={{ maxHeight: 260, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
            {!townView && loadingContacts ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={18} sx={{ color: colors.accent }} />
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>Loading client info</Typography>
              </Box>
            ) : rows.map((row) => (
              <Box
                key={row.parcel_id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: townView ? '1fr' : '1.1fr 1fr 1.3fr',
                  gap: 1,
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: `0.5px solid ${colors.borderTertiary}`,
                  '&:first-of-type': { pt: 0 },
                  '&:last-child': { borderBottom: 'none', pb: 0 },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: 12, fontWeight: 500, color: colors.textPrimary }}>
                    {row.Address || row.parcel_id}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: 11, color: colors.textTertiary }}>{row.parcel_id}</Typography>
                </Box>
                {!townView && RECIPIENT_FIELDS.map(([field, placeholder]) => (
                  <TextField
                    key={field}
                    size="small"
                    placeholder={placeholder}
                    value={activeRecipients[row.parcel_id]?.[field] || ''}
                    onChange={(e) => setRecipients((prev) => ({ ...prev, [row.parcel_id]: { ...prev[row.parcel_id], [field]: e.target.value } }))}
                    error={field === 'email' && invalidEmailIds.includes(row.parcel_id)}
                    disabled={isTest}
                    sx={FIELD_SX}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </OptionsPanel>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
          {!devStoreOnly && !loadingContacts && missingEmailCount > 0 && (
            <Typography sx={{ fontSize: 12, color: colors.warn, mr: 'auto' }}>
              {townView
                ? `${missingEmailCount} propert${missingEmailCount === 1 ? 'y is' : 'ies are'} missing a client contact`
                : `${missingEmailCount} recipient${missingEmailCount === 1 ? '' : 's'} missing a valid email`}
            </Typography>
          )}
          <Button onClick={onClose} disabled={submitting} sx={CANCEL_BUTTON_SX}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disableElevation
            disabled={!canSubmit}
            startIcon={(loadingContacts || submitting) && <CircularProgress size={14} color="inherit" />}
            sx={PRIMARY_BUTTON_SX}
          >
            {submitting ? (townView ? 'Submitting...' : 'Creating...') : loadingContacts ? 'Loading...' : (townView ? 'Submit' : `Request ${docType.name}`)}
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default DocRequestDialog;
